import React from "react";
import {makeStyles} from "@material-ui/core/styles";
import {Button, TextField, Typography} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import {useTranslation} from "react-i18next";
import Flex from "components/grid/Flex";
import * as colors from "components/colors/Colors";

const useStyles = makeStyles((theme) => ({
    root: {
        flexDirection: "column",
        gap: theme.spacing(1),
        padding: theme.spacing(0, 2, 2, 2),
    },
    labelRow: {
        justifyContent: "space-between",
        alignItems: "center",
    },
    label: {
        fontWeight: 700,
        fontSize: "0.9rem",
    },
    originalMarker: {
        fontSize: "0.75rem",
        color: colors.clinicalMutedText,
    },
    editedMarker: {
        fontSize: "0.75rem",
        fontWeight: 700,
        color: colors.clinicalWarningText,
    },
    letterTextField: {
        background: colors.htmlWhite,
        "& textarea": {
            lineHeight: 1.6,
        },
    },
    actionRow: {
        alignItems: "center",
        gap: theme.spacing(1),
    },
    copyButton: {
        background: colors.clinicalAccentTeal,
        color: colors.htmlWhite,
        textTransform: "none",
        "&:hover": {background: colors.clinicalAccentTealHover},
    },
    resetButton: {
        textTransform: "none",
    },
    characterCount: {
        marginLeft: "auto",
        fontSize: "0.75rem",
        color: colors.clinicalMutedText,
    },
}));

const LetterTextEditor = ({label, aiLetterText, editedLetterText, onLetterTextChange, onLetterTextReset, onLetterTextCopy, isLetterTextCopyConfirmed}) => {
    const classes = useStyles();
    const {t: translate} = useTranslation();

    const originalLetterText = typeof aiLetterText === "string" ? aiLetterText : "";
    const shownLetterText = typeof editedLetterText === "string" ? editedLetterText : originalLetterText;
    const isLetterTextEdited = shownLetterText !== originalLetterText;

    const askCopyConfirmationForSelectedLetterText = (letterTextCopyEvent) => {
        if (isLetterTextCopyConfirmed) {
            return;
        }
        letterTextCopyEvent.preventDefault();
        const letterTextArea = letterTextCopyEvent.target;
        const selectedLetterText = typeof letterTextArea.value === "string"
            ? letterTextArea.value.substring(letterTextArea.selectionStart, letterTextArea.selectionEnd)
            : "";
        if (selectedLetterText) {
            onLetterTextCopy(selectedLetterText);
        }
    };

    return (
        <Flex container className={classes.root}>
            <Flex container className={classes.labelRow}>
                <Typography className={classes.label}>{label}</Typography>
                <Typography className={isLetterTextEdited ? classes.editedMarker : classes.originalMarker}>
                    {translate(isLetterTextEdited
                        ? "clinicalReportSummarizer.letterText.edited"
                        : "clinicalReportSummarizer.letterText.original")}
                </Typography>
            </Flex>
            <TextField
                multiline
                minRows={6}
                variant={"outlined"}
                fullWidth
                value={shownLetterText}
                onChange={(letterTextChangeEvent) => onLetterTextChange(letterTextChangeEvent.target.value)}
                InputProps={{className: classes.letterTextField}}
                inputProps={{
                    spellCheck: true,
                    onCopy: askCopyConfirmationForSelectedLetterText,
                    onCut: askCopyConfirmationForSelectedLetterText,
                    onDragStart: askCopyConfirmationForSelectedLetterText,
                }}
            />
            <Flex container className={classes.actionRow}>
                <Button
                    variant={"contained"}
                    className={classes.copyButton}
                    startIcon={<FileCopyIcon />}
                    onClick={() => onLetterTextCopy(shownLetterText)}
                >
                    {translate("clinicalReportSummarizer.letterText.copy")}
                </Button>
                <Button
                    variant={"outlined"}
                    className={classes.resetButton}
                    disabled={!isLetterTextEdited}
                    onClick={onLetterTextReset}
                >
                    {translate("clinicalReportSummarizer.letterText.reset")}
                </Button>
                <Typography className={classes.characterCount}>
                    {translate("clinicalReportSummarizer.letterText.characterCount", {characterCount: shownLetterText.length})}
                </Typography>
            </Flex>
        </Flex>
    );
};

export default LetterTextEditor;
